// Created By System on 29-Jan-2026
// Mounting Structure BOM View Component
import React from "react";
import { Badge, Col, Row, Table, Card } from "react-bootstrap";
import { formatDateForView } from "../../utils/helper";

const MountingStructureView = ({ bom, items }) => {
  // Normalize roof type value - ensures consistent format for display
  const normalizeRoofType = (roofType) => {
    if (!roofType) return null;
    
    // Handle React Select object
    const rawValue = roofType?.value || roofType;
    
    // Guard against null/undefined/empty objects
    if (!rawValue || (typeof rawValue === 'object' && Object.keys(rawValue).length === 0)) {
      return null;
    }
    
    // Convert to string, trim, uppercase, remove spaces
    const normalized = rawValue?.toString().trim().toUpperCase().replace(/\s+/g, '');
    
    // Map "TINSHED" variations to standard "TINSHED" (already normalized, but ensure consistency)
    if (normalized === 'TINSHED') {
      return 'TINSHED';
    }
    
    // Return normalized value if valid
    const validTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
    return validTypes.includes(normalized) ? normalized : null;
  };

  // Parse parts from JSONB - safely handles string or array
  // Updated by Farhan Khan on 31-Jan-2026
  // Fixed By System on 06-Feb-2026 - Handle parts from mounting_structure_parts table (arrays)
  const parseParts = (parts) => {
    if (!parts) return [];
    if (Array.isArray(parts)) return parts;
    if (typeof parts === 'string') {
      try {
        const parsed = JSON.parse(parts);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('Failed to parse parts JSONB:', e);
        return [];
      }
    }
    return [];
  };

  // Normalize parts array - ensures clean structure
  // Updated by Farhan Khan on 31-Jan-2026
  // Fixed By System on 06-Feb-2026 - Handle parts from mounting_structure_parts table
  const normalizeParts = (parts) => {
    const parsed = parseParts(parts);
    return parsed
      .filter(part => part !== null && part !== undefined)
      .map(part => {
        // Handle shape and make - they can be arrays (from mounting_structure_parts) or strings
        let shapeValue = part.shape || "";
        let makeValue = part.make || "";
        
        // If shape/make are arrays, convert to comma-separated string for display
        if (Array.isArray(shapeValue)) {
          shapeValue = shapeValue.filter(s => s).join(", ");
        }
        if (Array.isArray(makeValue)) {
          makeValue = makeValue.filter(m => m).join(", ");
        }
        
        return {
          component_name: part.component_name || part.part_name || "",
          specs: part.specs || "",
          size: part.size || "",
          shape: shapeValue,
          make: makeValue,
          others: part.others || ""
        };
      });
  };

  // Parse JSON array string to array for display
  const parseJsonArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Format array for display
  const formatArrayDisplay = (value) => {
    const arr = parseJsonArray(value);
    return arr.length > 0 ? arr.join(", ") : "-";
  };
  
  // Get display label for roof type (show "TIN SHED" for "TINSHED")
  const getRoofTypeLabel = (roofType) => {
    const normalized = normalizeRoofType(roofType);
    if (normalized === 'TINSHED') {
      return 'TIN SHED';
    }
    return normalized || '-';
  };

  return (
    <>
      {/* BOM Header Information */}
      <Row className="py-3 ibs-edit-form">
        <Col lg={6}>
          <label>BOM Name</label>
          <span>{bom.name || "-"}</span>
        </Col>
        <Col lg={6}>
          <label>Status</label>
          <span>
            <Badge bg={bom.status === "Active" ? "success" : "secondary"}>
              {bom.status || "-"}
            </Badge>
          </span>
        </Col>
        <Col lg={6}>
          <label>Created Date</label>
          <span>{formatDateForView(bom?.createddate || "")}</span>
        </Col>
        <Col lg={6}>
          <label>Last Modified Date</label>
          <span>{formatDateForView(bom?.lastmodifieddate || "")}</span>
        </Col>
      </Row>

      {/* Line Items Display */}
      <Row className="view-form gx-5 mx-auto mt-4">
        <Col lg={12}>
          <Row className="view-form-header bg-custom align-items-center">
            <Col lg={12}>
              <h5>Mounting Structure Line Items</h5>
            </Col>
          </Row>

          {(!items || items.length === 0) ? (
            <div className="text-center my-4 p-4 border rounded">
              <p className="text-muted mb-0">No line items found.</p>
            </div>
          ) : (
            items.map((lineItem, lineItemIndex) => {
              // Parse and normalize parts from JSONB
              // Updated by Farhan Khan on 31-Jan-2026
              const normalizedParts = normalizeParts(lineItem.parts);
              const hasParts = normalizedParts && Array.isArray(normalizedParts) && normalizedParts.length > 0;
              
              return (
                <Card key={lineItem.id || lineItemIndex} className="mb-4 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">S.No {lineItem.s_no ?? lineItemIndex + 1} | Line Item #{lineItemIndex + 1}</h6>
                  </Card.Header>
                  <Card.Body>
                    {/* ROOF TYPE */}
                    <Row className="mb-3">
                      <Col lg={6}>
                        <label className="fw-bold">ROOF TYPE:</label>
                        <span className="ms-2">{getRoofTypeLabel(lineItem.roof_type)}</span>
                      </Col>
                      <Col lg={6}>
                        <label className="fw-bold">Status:</label>
                        <span className="ms-2">
                          <Badge bg={lineItem.status === "Active" ? "success" : "secondary"}>
                            {lineItem.status || "-"}
                          </Badge>
                        </span>
                      </Col>
                    </Row>

                    {/* STRUCTURE HEIGHT */}
                    <Row className="mb-3">
                      <Col lg={6}>
                        <label className="fw-bold">STRUCTURE HEIGHT - FRONT (FT):</label>
                        <span className="ms-2">
                          {lineItem.structure_height_front !== null &&
                          lineItem.structure_height_front !== undefined
                            ? `${lineItem.structure_height_front} FT`
                            : "-"}
                        </span>
                      </Col>
                      <Col lg={6}>
                        <label className="fw-bold">STRUCTURE HEIGHT - BACK (FT):</label>
                        <span className="ms-2">
                          {lineItem.structure_height_back !== null &&
                          lineItem.structure_height_back !== undefined
                            ? `${lineItem.structure_height_back} FT`
                            : "-"}
                        </span>
                      </Col>
                    </Row>

                    {/* PART DETAILS */}
                    {hasParts && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">PART DETAILS</h6>
                          <Table striped bordered hover size="sm">
                            <thead className="bg-custom text-white">
                              <tr>
                                <th>PART NAME</th>
                                <th>SPECS</th>
                                <th>SIZE</th>
                                <th>SHAPE</th>
                                <th>MAKE</th>
                                <th>OTHERS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {normalizedParts.map((part, partIndex) => (
                                <tr key={partIndex}>
                                  <td>{part.component_name || "-"}</td>
                                  <td>{part.specs || "-"}</td>
                                  <td>{part.size || "-"}</td>
                                  <td>{part.shape || "-"}</td>
                                  <td>{part.make || "-"}</td>
                                  <td>{part.component_name === "OTHERS" ? (part.others || "-") : "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    )}

                    {/* Legacy structure - display component fields if no parts */}
                    {!hasParts && lineItem.component_name && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">PART DETAILS</h6>
                          <Table striped bordered hover size="sm">
                            <thead className="bg-custom text-white">
                              <tr>
                                <th>PART NAME</th>
                                <th>SPECS</th>
                                <th>SIZE</th>
                                <th>SHAPE</th>
                                <th>MAKE</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>{lineItem.component_name || "-"}</td>
                                <td>{formatArrayDisplay(lineItem.specs)}</td>
                                <td>{lineItem.size || "-"}</td>
                                <td>{formatArrayDisplay(lineItem.shape)}</td>
                                <td>{formatArrayDisplay(lineItem.make)}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    )}

                    {/* TIN SHED STRUCTURE */}
                    {normalizeRoofType(lineItem.roof_type) === "TINSHED" && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">TIN SHED STRUCTURE</h6>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">STRUCTURE TYPE:</label>
                          <span className="ms-2">
                            {lineItem.tin_shed_structure_type || "-"}
                          </span>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">MOUNTING TYPE:</label>
                          <span className="ms-2">
                            {lineItem.tin_shed_mounting_type || "-"}
                          </span>
                        </Col>
                      </Row>
                    )}

                    {/* COMBO STRUCTURE */}
                    {normalizeRoofType(lineItem.roof_type) === "COMBO" && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">COMBO STRUCTURE</h6>
                        </Col>
                        <Col lg={4}>
                          <label className="fw-bold">NUMBER OF PANELS - RCC:</label>
                          <span className="ms-2">
                            {lineItem.combo_panels_rcc !== null &&
                            lineItem.combo_panels_rcc !== undefined
                              ? lineItem.combo_panels_rcc
                              : "-"}
                          </span>
                        </Col>
                        <Col lg={4}>
                          <label className="fw-bold">NUMBER OF PANELS - GROUND:</label>
                          <span className="ms-2">
                            {lineItem.combo_panels_ground !== null &&
                            lineItem.combo_panels_ground !== undefined
                              ? lineItem.combo_panels_ground
                              : "-"}
                          </span>
                        </Col>
                        <Col lg={4}>
                          <label className="fw-bold">NUMBER OF PANELS - TINSHED:</label>
                          <span className="ms-2">
                            {lineItem.combo_panels_tinshed !== null &&
                            lineItem.combo_panels_tinshed !== undefined
                              ? lineItem.combo_panels_tinshed
                              : "-"}
                          </span>
                        </Col>
                      </Row>
                    )}

                    {/* SPECIAL STRUCTURE */}
                    {lineItem.special_structure_type && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">SPECIAL STRUCTURE</h6>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">TYPE:</label>
                          <span className="ms-2">
                            {lineItem.special_structure_type || "-"}
                          </span>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">LENGTH:</label>
                          <span className="ms-2">
                            {lineItem.special_structure_length !== null &&
                            lineItem.special_structure_length !== undefined
                              ? lineItem.special_structure_length
                              : "-"}
                          </span>
                        </Col>
                        <Col lg={12}>
                          <label className="fw-bold">SPECS:</label>
                          <span className="ms-2">
                            {lineItem.special_structure_specs || "-"}
                          </span>
                        </Col>
                        {lineItem.special_structure_type === "CLEANING SYSTEM" && (
                          <Col lg={12}>
                            <label className="fw-bold">CLEANING SYSTEM:</label>
                            <span className="ms-2">
                              {lineItem.cleaning_system || "-"}
                            </span>
                          </Col>
                        )}
                        {lineItem.special_structure_type === "OTHERS" && (
                          <Col lg={12}>
                            <label className="fw-bold">OTHERS:</label>
                            <span className="ms-2">
                              {lineItem.others_text || "-"}
                            </span>
                          </Col>
                        )}
                      </Row>
                    )}

                    {/* Legacy special structure display - Fixed By System on 06-Feb-2026 - Map DB fields correctly */}
                    {!lineItem.special_structure_type && lineItem.item_type === 'SPECIAL_STRUCTURE' && (
                      <Row className="mb-3">
                        <Col lg={12}>
                          <h6 className="mb-3">SPECIAL STRUCTURE</h6>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">TYPE:</label>
                          <span className="ms-2">
                            {lineItem.special_structure_type || "-"}
                          </span>
                        </Col>
                        <Col lg={6}>
                          <label className="fw-bold">LENGTH:</label>
                          <span className="ms-2">
                            {/* Fixed By System on 06-Feb-2026 - Check both length (DB) and special_structure_length (mapped) */}
                            {(lineItem.length !== null && lineItem.length !== undefined)
                              ? lineItem.length
                              : (lineItem.special_structure_length !== null && lineItem.special_structure_length !== undefined)
                                ? lineItem.special_structure_length
                                : "-"}
                          </span>
                        </Col>
                        <Col lg={12}>
                          <label className="fw-bold">SPECS:</label>
                          <span className="ms-2">
                            {/* Fixed By System on 06-Feb-2026 - Check both specs (DB) and special_structure_specs (mapped) */}
                            {lineItem.special_structure_specs || lineItem.specs || "-"}
                          </span>
                        </Col>
                        <Col lg={12}>
                          <label className="fw-bold">NOTES:</label>
                          <span className="ms-2">
                            {/* Fixed By System on 06-Feb-2026 - Map notes to cleaning_system or others_text based on type */}
                            {lineItem.cleaning_system || lineItem.others_text || lineItem.notes || "-"}
                          </span>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              );
            })
          )}
        </Col>
      </Row>
    </>
  );
};

export default MountingStructureView;
